import { reactive, ref, computed,inject, nextTick, onMounted, watchEffect } from "vue";

const axios = require("axios");

let displayWidth = ref(0);
let displayHeight = ref(0);
let windowScrollTop = ref(0);
let responsiveClassMap = config.style.responsive.classMap;

let largerAndSmallerComputedProperties = {};
for (const [key, value] of Object.entries(responsiveClassMap)) {
  let smallerKey = `isSmallerThan${utilities.ucFirst(key)}`;
  let largerKey = `isLargerThan${utilities.ucFirst(key)}`;

  largerAndSmallerComputedProperties[smallerKey] = computed(() => {
    return displayWidth.value <= value;
  });
  largerAndSmallerComputedProperties[largerKey] = computed(() => {
    return displayWidth.value > value;
  });
}

let isMobile = computed(() => {
  let targetBreakPoint = config.style.responsive.mainMobileBreakPoint;
  let computedPropertyName = `isSmallerThan${utilities.ucFirst(
    targetBreakPoint
  )}`;

  if (
    !largerAndSmallerComputedProperties.hasOwnProperty(computedPropertyName)
  ) {
    warn(
      "dom helper - can not compute is mobile because the computed property is not available. Check your style.responsive configuration"
    );
    return true;
  }
  return largerAndSmallerComputedProperties[computedPropertyName].value;
});

let updateDisplaySizeFromDom = () => {
  if (utilities.isSSR()) {
    const isMobileUa = inject('isMobile', true);
    displayWidth.value = isMobileUa ? 360 : 1400;
    displayHeight.value = isMobileUa ? 667 : 900;
  } else {
    displayWidth.value = window.innerWidth;
    displayHeight.value = window.innerHeight;
  }

  return true;
};
let displaySizeDebounce = null;

// set initial display size immediatly
updateDisplaySizeFromDom();

if (!utilities.isSSR()) {
  window.addEventListener(
    "scroll",
    (e) => {
      windowScrollTop.value = window.scrollY;
    },
    { passive: true }
  );

  //  watch for changes window size changes
  displaySizeDebounce = utilities.debounce(updateDisplaySizeFromDom, 100, {
    returnType: "all",
  });
  window.addEventListener("resize", function () {
    displaySizeDebounce.clear();
    displaySizeDebounce.run();
  });
}

const scrollToElement = (hashOrElement, options = { offset: 120 }) => {
  if (utilities.isSSR()) {
    return true;
  }

  let element = hashOrElement;

  if (typeof element === "string") {
    if (!hashOrElement.startsWith("#")) {
      hashOrElement = "#" + hashOrElement;
    }
    element = document.querySelector(hashOrElement);
  }

  if (!(element instanceof Element)) {
    warn(
      "useDom().scrollToElement: first argument must be a valid existing hash for an element, or an actual dom element",
      { arg1: hashOrElement }
    );
  }

  if (!element) {
    return true;
  }

  const y =
    element.getBoundingClientRect().top + window.scrollY - options.offset;
  window.scrollTo({ top: y, behavior: "smooth" });
};

const initiateDownload = async (url, name = "") => {
  if (utilities.isSSR()) {
    return false;
  }

  const response = await axios.get(url, { responseType: "blob" });

  let anchor = document.createElement("a");
  document.body.appendChild(anchor);
  anchor.href = window.URL.createObjectURL(new Blob([response.data]));
  anchor.target = "_self";
  anchor.download = name; // do we need extension?
  anchor.style.display = "none";

  anchor.click();

  nextTick(() => {
    document.body.removeChild(anchor);
  });
};

const scrollSpyObjects = {};
let scrollSpyObjectIndex = 0;
const updateScrollSpy = (ssObject) => {
  ssObject.items.forEach((item) => {
    item.isActive = false;
    item.isExactActive = false;
    if (!item.el) {
      return true;
    }

    let topOffset = item.el.getBoundingClientRect().top;
    item.isActive = topOffset < ssObject.options.offset;
  });

  // now each item active is set. we need to find the "last" one that is like that
  let index = 0;
  ssObject.items.forEach((item) => {
    // no active items -> no currentActive item.
    if (index === 0 && !item.isActive) {
      return false;
    }

    // last item is active - last item it is
    if (index === ssObject.items.length - 1 && item.isActive) {
      item.isExactActive = true;
      return false;
    }

    // first item is active, but last item is not.
    // find the first inactive item. the one before it is the exactActive item;
    if (!item.isActive) {
      // this is the one. we accoutned already for item 0 being inactive;
      ssObject.items[index - 1].isExactActive = true;
      return false;
    }
    // ... lets continue to next item
    index = index + 1;
  });
};
const createScrollSpy = (ids, options = { offset: 120 }) => {
  let result = reactive({
    options,
    hashMap: {},
    items: [],
    id: scrollSpyObjectIndex,
    lastScroll: 0,
    destroy() {
      //deactivate listener
      // delete reference
    },
    update() {
      updateScrollSpy(result);
    },
    destroyWatcher: undefined,
  });

  ids.forEach((id) => {
    id = id.replace("#", "");
    let item = {
      id: id,
      selector: "#" + id,
      el: undefined,
      isActive: false,
      isExactActive: false,
    };

    result.hashMap[id] = item;
    result.items.push(item);
  });

  if (utilities.isSSR()) {
    return result;
  }

  // notice: this must be callable many times. Each later call is an update
  result.setup = () => {
    if (typeof result.destroyWatcher === "function") {
      result.destroyWatcher();
    }
    // find and set the element
    Object.values(result.hashMap).forEach((item) => {
      item.el = document.querySelector(item.selector);
    });

    let debouncedUpdate = utilities.debounce(() => {
      updateScrollSpy(result);
    }, 100);

    result.destroyWatcher = watchEffect(() => {
      let current = windowScrollTop.value;
      let last = result.lastScroll;

      if (current !== last) {
        debouncedUpdate();
      }
    });

    return result;
  };
  // we are "live" we need to do the "real work" since we are on client
  scrollSpyObjects[scrollSpyObjectIndex] = result;
  scrollSpyObjectIndex = scrollSpyObjectIndex + 1;

  // we can set this up on mounted. If we can set an onMounted - good for us
  onMounted(() => {
    result.setup();
  });

  return result;
};

const isWindowScrolled = computed(() => {
  return windowScrollTop.value > 0;
});

export default () => {
  return {
    displayWidth,
    displayHeight,
    ...largerAndSmallerComputedProperties,
    isMobile,
    scrollToElement,
    initiateDownload,
    isWindowScrolled,
    windowScrollTop,
    createScrollSpy,
  };
};
